import { z } from "zod";

import { solutionRecommendationsSchema } from "./solution-recommendations.js";

/**
 * The Match Validator's run summary. Lives in
 * `jobs.workerResult.artifacts[0]` once the orchestrator persists the
 * agent's `run_complete` payload via `applyAgentOutput` (see
 * `services/orchestrator/src/routes/callbacks.ts`).
 *
 * The agent-type is `"match-validator"` (hyphen) so the orchestrator's
 * paired-validator-hop check (`agent.agentType.endsWith("_validator")`)
 * treats this dispatch as a worker, not a paired validator hop. Job
 * state transitions through `running → completed`; the Analyst-retry
 * chain (`maybeChainAnalystRetryOnMatchValidatorIncomplete`) fires off
 * the success arm via `setImmediate`, not via a validator hop.
 *
 * Used by both the agent service (`services/match-validator/`) and the
 * orchestrator's `applyMatchValidatorOutcome` so the wire contract is
 * single-sourced. Issue #143.
 *
 * `enrichedRecommendations` is the payload to write back to
 * `solution_presentations.recommendations` verbatim — the validator has
 * stamped each `tools[*]` entry with `match_validation_score`,
 * `match_validation_status`, `removal_reason`, and `match_check_outcomes`
 * as appropriate, and set the solution-level
 * `ranking_order_assessment`. The Analyst's original per-rec fields
 * (`objective_score`, `rationale`, `addresses_objective_indices`) are
 * preserved.
 *
 * `excludedToolIds` is the union of `toolId`s that failed
 * constraint-compliance (MV1) or problem-relevance (MV3) and were
 * marked `removed`. It is the context the orchestrator forwards to the
 * Analyst on the single permitted retry when `allRemoved` is true, so
 * the retry can't pick the same offending tools again.
 *
 * `allRemoved` is true iff every entry in `enrichedRecommendations.tools`
 * has `match_validation_status === "removed"` AND there is no
 * `custom_proposal_narrative` (i.e. the presentation is now empty of
 * client-deliverable content). Driving the retry decision off this flag
 * rather than recomputing in `applyAgentOutput` keeps the semantic
 * single-sourced in the worker.
 *
 * Excludes the run-level metadata that's already on `AgentOutput`
 * itself (errors, callSummary, durationSeconds) — those live on the
 * envelope, not in the artifact.
 */
export const matchValidatorArtifactSchema = z.object({
  /** Discriminator carried for forward-compat. */
  kind: z.literal("match_validation"),
  /** The `problem_memos.id` whose presentation was just judged. */
  memoId: z.string().uuid(),
  /** The `solution_presentations.id` the validator just judged. */
  presentationId: z.string().uuid(),
  /** Parent validation status to write to `match_validation_status`. */
  validationStatus: z.enum(["validated", "incomplete", "flagged"]),
  /** 0.00–1.00 fraction of recs kept (passed the removal checks). */
  validationScore: z.number().min(0).max(1),
  /** Mutated payload to persist verbatim to the presentation row. */
  enrichedRecommendations: solutionRecommendationsSchema,
  /** Tools the Analyst should NOT re-propose on a retry. */
  excludedToolIds: z.array(z.string().uuid()),
  /**
   * Convenience flag driving the retry decision. See top-level docstring
   * for the precise definition.
   */
  allRemoved: z.boolean(),
  /**
   * Forensic snapshot of the `agent_config!A:B` rows that drove this
   * run. Mirrors the intake-validator and analyst artifacts —
   * thresholds + model id frozen so a later audit can interpret the
   * verdict against the templating values in force at the time.
   */
  agentConfigSnapshot: z.record(z.union([z.string(), z.number()])),
  /**
   * Git SHA of the seed file
   * `packages/prompts/src/match-validator/default.md` at run time, or
   * `"dev"` when running from an uncommitted tree. The seed is the
   * source for `agent_prompts.match-validator.default` — the live
   * prompt that drove the run is in `jobs.promptsSnapshot["default"]`.
   * This SHA is the seed-file provenance, not the live prompt's.
   */
  promptVersion: z.string().min(1),
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime(),
});

export type MatchValidatorArtifact = z.infer<typeof matchValidatorArtifactSchema>;
